/** How much a scanner can tell about the stream it has seen so far. */
export type JsonShape =
  /** Nothing but whitespace yet — the root character is still unknown. */
  | "pending"
  /** The stream does not start with `{` or `[`; no appended text can change that. */
  | "not-json"
  /** JSON-rooted, first top-level object still unterminated. */
  | "open"
  /** The first top-level object is complete. */
  | "closed";

interface JsonBoundaryScanner {
  /** Feeds the next slice of the stream. Costs time proportional to `delta`. */
  push(delta: string): JsonShape;
  /**
   * Index just past the first top-level object, counted across everything pushed.
   * Meaningful only once a push has returned `"closed"`; zero before that.
   */
  readonly endOffset: number;
}

/**
 * Resumable brace matcher for content that arrives in pieces: reports whether an
 * accumulating stream is JSON-rooted and where its first top-level object ends,
 * so a consumer never has to rescan everything it has received.
 *
 * Reports an offset rather than the substring so scanning stays O(1) in memory —
 * callers already hold the content and can slice it themselves.
 *
 * Quirks worth knowing, because they decide which object a caller parses: depth
 * counts only the bracket type the stream opened with, so an unbalanced inner
 * bracket of the other type ends the object early; and a backslash escapes the
 * next character outside strings as well as inside.
 */
export function createJsonBoundaryScanner(): JsonBoundaryScanner {
  let shape: JsonShape = "pending";
  let endOffset = 0;
  let consumed = 0;
  let depth = 0;
  let inString = false;
  let isEscaped = false;
  let openChar = "";
  let closeChar = "";

  const push = (delta: string): JsonShape => {
    if (shape === "not-json" || shape === "closed") return shape;

    const base = consumed;
    consumed += delta.length;

    let start = 0;
    if (shape === "pending") {
      while (start < delta.length && delta.charAt(start).trim() === "") start++;
      if (start === delta.length) return shape;

      const root = delta.charAt(start);
      if (root !== "{" && root !== "[") {
        shape = "not-json";
        return shape;
      }
      openChar = root;
      closeChar = root === "{" ? "}" : "]";
      shape = "open";
    }

    for (let i = start; i < delta.length; i++) {
      const char = delta.charAt(i);

      if (isEscaped) {
        isEscaped = false;
        continue;
      }
      if (char === "\\") {
        isEscaped = true;
        continue;
      }
      if (char === '"') {
        inString = !inString;
        continue;
      }
      if (inString) continue;

      if (char === openChar) {
        depth++;
      } else if (char === closeChar) {
        depth--;
        if (depth === 0) {
          endOffset = base + i + 1;
          shape = "closed";
          return shape;
        }
      }
    }

    return shape;
  };

  return {
    push,
    get endOffset() {
      return endOffset;
    },
  };
}
